import React, { useState, useEffect, useRef } from 'react';
import {
    Typography,
    Grid,
    Box,
    CssBaseline,
    Divider,
    TextField,
    Button,
    Modal,
    Chip,
} from '@mui/material';
import {
    EditNote,
    Phone,
    Cancel,
    DeleteForever,
} from '@mui/icons-material';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import { global_link } from '../Components/Globallink';
import modalStyle from '../Components/ModalStyle';

const Faqs = () => {
    const api = axios.create({baseURL: global_link});
    const [searchTerm, setSearchTerm] = useState('');
    const [openAddScriptModal, setOpenAddScriptModal] = useState(false);
    const [openEditScriptModal, setOpenEditScriptModal] = useState(false);
    const [scriptsData, setScriptsData] = useState([]);
    const [userLevel, setUserLevel] = useState("");
    const authToken = process.env.AUTH_TOKEN;
    const [formData, setFormData] = useState({});
    const [selectedItem, setSelectedItem] = useState(null);
    const [expandedCards, setExpandedCards] = useState({});

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

    const handleOpenAddScriptModal = () => {
        setOpenAddScriptModal(true);
    }
    const handleCloseAddScriptModal = () => {
        setOpenAddScriptModal(false);
    }

    const handleOpenEditScriptModal = (item) => {
        setSelectedItem(item);
        setOpenEditScriptModal(true);
    }
    const handleCloseEditScriptModal = () => {
        setOpenEditScriptModal(false);
    }

    const handleScriptSubmit = (event) => {
        event.preventDefault();
        const formData = new FormData(event.target);
        const authyToken = localStorage.getItem(authToken);
        const headers = {
            Authorization: `Bearer ${authyToken}`,
            "Content-Type": "application/json",
        };
        api.post('/faqs/add', formData, { headers, withCredentials: true })
            .then((response) => {
                handleCloseAddScriptModal(false);
                fetchScripts();
            }).catch((error) => { console.error(error); });
    };

    const fetchScripts = async () => {
        try {
            const authyToken = localStorage.getItem(authToken);
            const response = await api.get('/faqs/getfaqs', {
                params: {
                    search: searchTerm
                },
                headers: {
                    Authorization: `Bearer ${authyToken}`,
                },
            });
            setScriptsData(response.data);
        } catch (error) {
            console.error('Error fetching Scripts:', error);
        }
    };

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setFormData((prevFormData) => ({
            ...prevFormData,
            [name]: value,
        }));
    };

    const handleInputModChange = (event) => {
        const { name, value } = event.target;
        setSelectedItem((prevFormState) => ({
            ...prevFormState,
            [name]: value,
        }));
    };

    const handleScriptEditSubmit = (event) => {
        event.preventDefault();
        const formData = new FormData(event.target);
        const authyToken = localStorage.getItem(authToken);
        const headers = {
            Authorization: `Bearer ${authyToken}`,
            "Content-Type": "application/json",
        };
        api.put(`/faqs/update/`, formData, { headers, withCredentials: true })
            .then((response) => {
                fetchScripts();
                handleCloseEditScriptModal();
            })
            .catch((error) => {
                console.error(error);
            });
    };

    const handleScriptDelete = (id) => {
        const authyToken = localStorage.getItem(authToken);
        const headers = {
            Authorization: `Bearer ${authyToken}`,
            "Content-Type": "application/json",
        };
        api.delete(`/faqs/delete/${id}`, { headers, withCredentials: true })
            .then((response) => {
                fetchScripts();
                handleCloseEditScriptModal();
            }).catch((error) => { console.error(error); });
    }

    const toggleReadMore = (id) => {
        setExpandedCards(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    const truncateText = (text, maxLength = 150) => {
        if (!text) return '';
        if (text.length <= maxLength) return text;
        return text.substr(0, maxLength) + '...';
    };

    useEffect(() => {
        fetchScripts();
    }, [searchTerm]);

    return (
        <>
            <CssBaseline />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <Typography variant="h5">FAQs</Typography>
                {sessionStorage.getItem("user_level") === "supervisor" && (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleOpenAddScriptModal()}
                        sx={{ marginLeft: 'auto', padding: '10px' }}
                    >
                        Add FAQs
                    </Button>
                )}
            </Box>

            <Box sx={{ pb: 3, pt: 2, display: 'flex', gap: 2 }}>
                <TextField
                    label="Search"
                    variant="outlined"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    placeholder="Search"
                    fullWidth
                />
            </Box>
            
            <Grid container spacing={1.7}>
                {scriptsData.map((row) => (
                    <Grid item xs={12} md={4} key={row.id}>
                        <Box sx={{
                            bgcolor: 'background.paper',
                            boxShadow: 'none',
                            border: `0.1px solid rgba(207, 211, 212, 0.2)`,
                            borderRadius: '5px',
                            padding: '10px',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            minHeight: '350px'
                        }}>
                            {row.header && <Chip label={row.header} />}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                                <Typography variant="h6" sx={{ flex: 1 }}>{row.question}</Typography>
                                {sessionStorage.getItem("user_level") === "supervisor" && (
                                    <Button
                                        variant="outlined"
                                        color="warning"
                                        onClick={() => handleOpenEditScriptModal(row)}
                                        sx={{ marginLeft: 'auto', minWidth: '40px' }}
                                    >
                                        <EditNote />
                                    </Button>
                                )}
                            </Box>
                            <Divider sx={{ marginTop: '10px', marginBottom: '10px' }} />
                            <Typography 
                                variant="body1" 
                                color="text.secondary" 
                                sx={{ 
                                    whiteSpace: 'pre-wrap', 
                                    fontSize: '15px', 
                                    fontWeight: '400',
                                    flex: 1,
                                    overflow: 'hidden'
                                }}
                            >
                                {expandedCards[row.id] ? row.answer : truncateText(row.answer, 150)}
                            </Typography>
                            {row.answer && row.answer.length > 150 && (
                                <Button 
                                    onClick={() => toggleReadMore(row.id)}
                                    sx={{ 
                                        mt: 1, 
                                        textTransform: 'none',
                                        alignSelf: 'flex-start'
                                    }}
                                    size="small"
                                    color="primary"
                                >
                                    {expandedCards[row.id] ? 'Read less' : 'Read more'}
                                </Button>
                            )}
                        </Box>
                    </Grid>
                ))}
            </Grid>

            <Modal
                open={openAddScriptModal}
                onClose={handleCloseAddScriptModal}
                aria-labelledby="modal-title"
                aria-describedby="modal-contact"
            >
                <Box sx={modalStyle}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                        <div className="modal-content">
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                <h3 id="modal-title">Add FAQs</h3>
                                <Box display="flex" justifyContent="right">
                                    <Cancel onClick={() => handleCloseAddScriptModal(false)} sx={{ cursor: "pointer" }} />
                                </Box>
                            </Box>
                            <form onSubmit={handleScriptSubmit}>
                                <TextField
                                    label="Header"
                                    placeholder="Header"
                                    variant="outlined"
                                    fullWidth
                                    name="header"
                                    onChange={handleInputChange}
                                    sx={{ marginTop: "15px" }}
                                />

                                <TextField
                                    label="Question"
                                    placeholder="Question"
                                    variant="outlined"
                                    fullWidth
                                    name="question"
                                    onChange={handleInputChange}
                                    sx={{ marginTop: "15px" }}
                                />

                                <TextField
                                    multiline
                                    rows={6}
                                    label="Answer"
                                    placeholder="Answer"
                                    variant="outlined"
                                    fullWidth
                                    name="answer"
                                    onChange={handleInputChange}
                                    sx={{ marginTop: "15px" }}
                                    required="true"
                                />
                                <Button type="submit" variant="contained" sx={{ padding: "10px", marginTop: "12px", color: 'white' }}>Submit FAQs</Button>
                            </form>
                        </div>
                    </Box>
                </Box>
            </Modal>

            <Modal
                open={openEditScriptModal}
                onClose={handleCloseEditScriptModal}
                aria-labelledby="modal-title"
                aria-describedby="modal-contact"
            >
                <Box sx={modalStyle}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                        <div className="modal-content">
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                <h3 id="modal-title">Edit FAQs</h3>
                                <Box display="flex" justifyContent="right">
                                    <Cancel onClick={() => handleCloseEditScriptModal(false)} sx={{ cursor: "pointer" }} />
                                </Box>
                            </Box>
                            <form onSubmit={handleScriptEditSubmit}>
                                {selectedItem && (
                                    <Grid container spacing={1}>
                                        <TextField
                                            label="Header"
                                            placeholder="Header"
                                            variant="outlined"
                                            fullWidth
                                            name="header"
                                            onChange={handleInputModChange}
                                            value={selectedItem.header || ""}
                                            sx={{ marginTop: "15px" }}
                                        />
                                        <TextField
                                            name="id"
                                            value={selectedItem.id || ""}
                                            sx={{ marginTop: "15px", display: "none!important" }}
                                        />

                                        <TextField
                                            label="Question"
                                            placeholder="Question"
                                            variant="outlined"
                                            fullWidth
                                            name="question"
                                            onChange={handleInputModChange}
                                            value={selectedItem.question || ""}
                                            sx={{ marginTop: "15px" }}
                                        />

                                        <TextField
                                            multiline
                                            rows={6}
                                            label="Answer"
                                            placeholder="Answer"
                                            variant="outlined"
                                            fullWidth
                                            name="answer"
                                            onChange={handleInputModChange}
                                            value={selectedItem.answer || ""}
                                            sx={{ marginTop: "15px" }}
                                            required="true"
                                        />

                                        <Grid container item xs={12} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Button type="submit" variant="outlined" color='warning' sx={{ padding: "10px", marginTop: "12px" }}>Edit FAQs</Button>
                                            <Button type="button" variant="contained" color="error" sx={{ padding: "10px", marginTop: "12px", color: 'white' }} onClick={() => handleScriptDelete(selectedItem.id)}>Delete FAQs</Button>
                                        </Grid>
                                    </Grid>
                                )}
                            </form>
                        </div>
                    </Box>
                </Box>
            </Modal>
        </>
    );
};

export default Faqs;